import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import Toolbar from "../../../CommonComponents/common/Toolbar";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import TopSection from "../../../CommonComponents/TopSection";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import CollapsibleHeader from "../../../CommonComponents/common/CollapsibleHeader";
import useRedirect from "../../../CustomHooks/useRedirect";
import {
  fetchHeaderAndLinesData,
  fetchDeliveryData,
  fetchItemsData,
  fetchOrdersData,
} from "../../../services/OutboundShipments/OutboundShipmentsDetailService";
import OutboundShipmentHeader from "./OutboundShipmentHeader";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";
import { formatValue } from "../../../utils/currencyUtils";
import { parseDateValue } from "../../../utils/commonFunctions";

const OutboundShipmentDetail = () => {
  const navigate = useNavigate();
  const redirect = useRedirect();
  const { id } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hotItemsOnly, setHotItemsOnly] = useState(false);
  const [hotOrdersOnly, setHotOrdersOnly] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [linesRows, setLinesRows] = useState([]);
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [itemsRows, setItemsRows] = useState([]);
  const [ordersRows, setOrdersRows] = useState([]);
  const [exceptionType, setExceptionType] = useState("");

  // Separate loading states for each section
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const breadcrumbArray = ['Demand', 'Outbound Shipments', 'Outbound Shipments Detail'];

  useEffect(() => {
    if (!id) return;

    const fetchHeadersData = async () => {
      // --- Header & Lines ---
      setLoadingDetails(true);
      setLoadingLines(true);
      try {
        const headerResult = await fetchHeaderAndLinesData(id);
        setHeaderData(headerResult);
        setExceptionType(headerResult?.exceptionType);
        setLinesRows(
          headerResult?.deliveryOutboundShipmentLine?.map(item => ({
            line_no: item.lineNo,
            ship_from: item?.shipFrom,
            from_site: item?.fromSite,
            ship_from_owner: item?.shipFromOwner,
            ship_to: item?.shipTo,
            to_site: item?.toSite,
            ship_to_owner: item?.shipToOwner,
            creation_date: item?.creationDate,
            container_ship_date: item?.containerShipDate,
            container_eta: item?.containerETA,
            container_pta: item?.containerPTA,
            no_of_deliveries: item?.numberOfDeliveries,
            line_state: item?.lineState,
            line_supplier: item?.lineSupplier,
            supplier_description: item?.supplierDescription,
            line_customer: item?.lineCustomer,
            customer_description: item?.customerDescription,
            place_of_delivery: item?.placeOfDelivery,
            gross_wt: formatValue(item?.grossWeight),
            line_exception: item?.lineException
          })) ?? []
        );
      } catch (err) {
        console.error("Header & Lines API failed:", err);
      } finally {
        setLoadingDetails(false);
        setLoadingLines(false);
      }
    };

    const fetchOrderData = async () => {
      setLoadingOrders(true);
      try {
        const ordersData = await fetchOrdersData(id);
        setOrdersRows(
          ordersData?.map(item => ({
            line_no: item?.lineNo,
            order_no: item?.orderNo,
            customer: item?.customer,
            customer_name: item?.customerName,
            created_by: item?.createdBy,
            supplier: item?.supplier,
            supplier_name: item?.supplierName,
            ship_to: item?.shipTo,
            ship_to_desc: item?.shipToSite,
            bill_to: item?.billTo,
            bill_to_description: item?.billToDescription,
            lob: item?.orderLine?.[0]?.lob,
            creation_date: item?.creationDate,
            header_state: item?.headerState,
            process_type: item?.processType,
            erp_order_type: item?.erpOrderType,
            lmd: item?.lmdDate,
            order_exception: item?.orderException,
            total_weight: formatValue(item?.totalWeightOrder),
            total_weight_uom: item?.totalWeightUOM,
          })) ?? []
        );
      } catch (err) {
        console.error("Orders API failed:", err);
      } finally {
        setLoadingOrders(false);
      }
    };

    const fetchDeliveriesData = async () => {
      setLoadingDeliveries(true);
      try {
        const deliveriesData = await fetchDeliveryData(id);
        setDeliveryRows(
          deliveriesData?.map(item => ({
            line_no: item.lineNo,
            delivery_no: item.deliveryNo,
            delivery_type: item.deliveryType,
            lob: item.lob,
            supplier: item.supplier,
            supplier_name: item.supplierName,
            customer: item.customer,
            customer_name: item.customerName,
            ship_from: item.shipFrom,
            ship_from_owner: item.shipFromOwner,
            ship_from_site: item.shipFromSite,
            ship_to: item.shipTo,
            ship_to_owner: item.shipToOwner,
            ship_to_site: item.shipToSite,
            shipment_no: item.shipmentNo,
            shipment_line_no: item.shipmentLineNo,
            ship_mode: item.shipMode,
            carrier: item?.carrier,
            planned_ship_date: item?.plannedShipDate,
            eta: item?.eta,
            delivery_exception: item?.deliveryException
          })) ?? []
        );
      } catch (err) {
        console.error("Delivery API failed:", err);
        setDeliveryRows([]);
      } finally {
        setLoadingDeliveries(false);
      }
    };

    const fetchItemData = async () => {
      setLoadingItems(true);
      try {
        const itemsData = await fetchItemsData(id);
        setItemsRows(
          itemsData?.map(item => ({
            line_no: item?.lineNo,
            supplier_item_name: item?.supplierItemName,
            ship_to: item?.shipTo,
            item: item?.item,
            shipped_qty: formatValue(item?.shippedQuantity),
          })) ?? []
        );
      } catch (err) {
        console.error("Items API failed:", err);
        setItemsRows([]);
      } finally {
        setLoadingItems(false);
      }
    };
    // Run header, orders, deliveries & items independently in parallel
    fetchHeadersData();
    fetchOrderData();
    fetchDeliveriesData();
    fetchItemData();
  }, [id]);

  const redirectFn = useMemo(() => {
    return (path, state) => {
      redirect(path, state);
    };
  }, [redirect]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/core/demand/shipment");
    }
  };

  const toolbarProps = {
    number: id,
    status: exceptionType,
    icon: <LocalMallIcon />,
    onBack: handleBack,
    isCollapsed,
    toggleHeader: () => setIsCollapsed((prev) => !prev),
  };

  const linesTab = useMemo(
    () => ({
      label: `Lines(${linesRows?.length})`,
      loading: loadingLines,
      columns: [
        { field: "line_no", headerName: "Line No" },
        // { field: "pallet_no", headerName: "Pallet No" },
        // { field: "seal_no", headerName: "Seal No" },
        { field: "from_site", headerName: "From(Site)" },
        { field: "ship_from_owner", headerName: "Ship From Owner" },
        { field: "ship_to", headerName: "Ship To" },
        { field: "to_site", headerName: "To(Site)" },
        { field: "ship_to_owner", headerName: "Ship To Owner" },
        // {
        //   field: "receiving_shipping_point",
        //   headerName: "Receiving Shipping Point",
        // },
        { field: "creation_date", headerName: "Creation Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "container_ship_date", headerName: "Container Ship Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "container_eta", headerName: "Container ETA", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "container_pta", headerName: "Container PTA", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "container_ata", headerName: "Container ATA" },
        // { field: "container_type", headerName: "Container Type" },
        //{ field: "no_of_deliveries", headerName: "No of Deliveries" },
        { field: "line_state", headerName: "Line State" },
        // { field: "hazmat_code", headerName: "Hazmat Code" },
        // { field: "temp_controlled", headerName: "Temp Controlled" },
        // { field: "pre_stop_scac_code", headerName: "Pre Stop Scac Code" },
        // { field: "pre_stop_carrier_name", headerName: "Pre Stop Carrier Name" },
        // { field: "post_stop_carrier_name", headerName: "Post Stop Carrier Name" },
        // { field: "post_stop_scac_code", headerName: "Post Stop Scac Code" },
        { field: "line_supplier", headerName: "Line Supplier" },
        { field: "supplier_description", headerName: "Supplier Description" },
        { field: "line_customer", headerName: "Line Customer" },
        { field: "customer_description", headerName: "Customer Description" },
        // { field: "freight_category", headerName: "Freight Category" },
        { field: "place_of_delivery", headerName: "Place of Delivery" },
        { field: "gross_wt", headerName: "Gross Wt" },
        // { field: "gross_wt_uom", headerName: "Gross Wt UOM" },
        // { field: "gross_volume", headerName: "Gross Volume" },
        // { field: "gross_volume_uom", headerName: "Gross Volume UOM" },
        // { field: "shipping_uom", headerName: "Shipping UOM" },
        // { field: "load_id", headerName: "Load Id" },
      ],
      rows: linesRows
    }),
    [linesRows, loadingLines]);

  const deliveriesTab = useMemo(
    () => ({
      label: `Deliveries(${deliveryRows.length})`,
      loading: loadingDeliveries,
      columns: [
        {
          field: "delivery_no",
          headerName: "Delivery No",
          cellRenderer: (params) => {
            const redirect = params.colDef.cellRendererParams?.redirect;
            return (
              <span
                onClick={() =>
                  redirect(`/core/demand/delivery/delivery-detail/${params.value}/${params.data.ship_from}`, {
                    menu: "Demand",
                    label: "Outbound Deliveries",
                    outerLabel: "Outbound Deliveries Detail",
                    previousPage: window.location.pathname,
                  })
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {params.value}
              </span>
            );
          },
          cellRendererParams: { redirect: redirectFn },
        },
        { field: "delivery_type", headerName: "Delivery Type" },
        { field: "lob", headerName: "LOB" },
        { field: "supplier", headerName: "Supplier" },
        { field: "supplier_name", headerName: "Supplier Name" },
        { field: "customer", headerName: "Customer" },
        { field: "customer_name", headerName: "Customer Name" },
        { field: "ship_from", headerName: "Ship From" },
        { field: "ship_from_owner", headerName: "Ship From Owner" },
        { field: "ship_from_site", headerName: "Ship From (Site)" },
        { field: "ship_to", headerName: "Ship To" },
        { field: "ship_to_owner", headerName: "Ship To Owner" },
        { field: "ship_to_site", headerName: "Ship To (Site)" },
        { field: "shipment_no", headerName: "Shipment No" },
        { field: "shipment_line_no", headerName: "Shipment Line No" },
        { field: "ship_mode", headerName: "Ship Mode" },
        // { field: "bol_no", headerName: "BOL No" },
        // { field: "hawb_no", headerName: "HAWB No" },
        { field: "carrier", headerName: "Carrier" },
        // { field: "container_no", headerName: "Container No" },
        // { field: "packing_slip_no", headerName: "Packing Slip No" },
        { field: "planned_ship_date", headerName: "Planned Ship Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "actual_ship_date", headerName: "Actual Ship Date" },
        { field: "eta", headerName: "ETA", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "ata", headerName: "ATA" },
        // { field: "truck_no", headerName: "Truck No" },
        // { field: "trailer_no", headerName: "Trailer No" },
        // { field: "comments", headerName: "Comments" },
      ],
      rows: deliveryRows
    }),
    [deliveryRows, loadingDeliveries, redirectFn]
  );

  const filteredItemsRows = useMemo(() => {
    let rows = itemsRows;
    // Filter items based on the toggle value
    rows = hotItemsOnly ? rows.filter((row) => row.hotOrder) : rows;
    return rows;
  }, [itemsRows, hotItemsOnly]);

  const itemsTab = useMemo(
    () => ({
      label: `Items(${filteredItemsRows.length})`,
      loading: loadingItems,
      columns: [
        { field: "supplier_item_name", headerName: "Material Description", flex: 1 },
        { field: "ship_to", headerName: "Ship To", flex: 1 },
        {
          field: "item",
          headerName: "Material Number",
          flex: 1,
          cellRenderer: (params) => {
            const { value } = params;
            return <span style={{ fontWeight: 500 }}>{value}</span>;
          },
        },
        {
          field: "shipped_qty",
          headerName: "Shipped Qty",
          flex: 1
        },
      ],
      rows: filteredItemsRows,
      toggle: {
        label: "Show only Hot Items",
        checked: hotItemsOnly,
        onChange: () => setHotItemsOnly(!hotItemsOnly),
      },
    }),
    [filteredItemsRows, hotItemsOnly, loadingItems]
  );

  const filteredOrdersRows = useMemo(() => {
    let rows = ordersRows;
    rows = hotOrdersOnly ? rows.filter((row) => row.hotOrder) : rows;
    return rows;
  }, [ordersRows, hotOrdersOnly]);

  const ordersTab = useMemo(
    () => ({
      label: `Orders(${filteredOrdersRows.length})`,
      loading: loadingOrders,
      columns: [
        {
          field: "order_no",
          headerName: "Order No",
          cellRenderer: (params) => {
            const { value } = params;
            return (
              <span
                onClick={() =>
                  redirectFn(`/core/demand/order/order-detail/${value}`, {
                    menu: "Demand",
                    label: "Sales Orders",
                    outerLabel: "Sales Orders Detail",
                    previousPage: window.location.pathname,
                  })
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            );
          },
        },
        { field: "customer", headerName: "Customer" },
        { field: "customer_name", headerName: "Customer Name" },
        { field: "created_by", headerName: "Created By", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "supplier", headerName: "Supplier" },
        { field: "supplier_name", headerName: "Supplier Name" },
        { field: "ship_to", headerName: "Ship To" },
        {
          field: "ship_to_desc",
          headerName: "Ship To Description",
        },
        { field: "bill_to", headerName: "Bill To" },
        { field: "bill_to_description", headerName: "Bill To Description" },
        { field: "lob", headerName: "LOB" },
        // { field: "inco_terms", headerName: "Inco Terms" },
        { field: "creation_date", headerName: "Creation Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "amount", headerName: "Amount" },
        // { field: "currency", headerName: "Currency" },
        // { field: "data_exceptions", headerName: "Data Exceptions" },
        { field: "header_state", headerName: "Header State" },
        // { field: "notes", headerName: "Notes" },
        { field: "process_type", headerName: "Process Type" },
        { field: "erp_order_type", headerName: "ERP Order Type" },
        // { field: "payment_terms", headerName: "Payment Terms" },
        // { field: "lct_order_type", headerName: "LCT Order Type" },
        { field: "lmd", headerName: "Last Modified Date", comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        { field: "total_weight", headerName: "Total Weight" },
        { field: "total_weight_uom", headerName: "Total Weight UOM" },
      ],
      rows: filteredOrdersRows,
      toggle: {
        label: "Show Only Hot Orders",
        checked: hotOrdersOnly,
        onChange: () => setHotOrdersOnly(!hotOrdersOnly),
      },
    }),
    [filteredOrdersRows, hotOrdersOnly, loadingOrders, redirectFn]
  );

  const tabs = [linesTab, ordersTab, deliveriesTab, itemsTab];

  return (
    <div>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={false}
        headerName=""
        hideTitle={true}
      />
      <Toolbar {...toolbarProps} />
      <CollapsibleHeader
        upperLayer={
          <Box sx={{ position: "relative" }}>
            {loadingDetails && <SectionSpinner />}
            <OutboundShipmentHeader
              hProps={headerData}
              linesData={linesRows}
              deliveryData={deliveryRows} // pass delivery data
            />
          </Box>
        }
        lowerLayer={< DetailsPageLowerLayer tabs={tabs} />}
        isCollapsed={isCollapsed}
      />
    </div >
  );
};

export default OutboundShipmentDetail;
